import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge, fmtMoney, EmptyState } from "@/components/Shared";
import { CalendarBlank } from "@phosphor-icons/react";

const monthsEs = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];

function formatSpanishDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  const wk = ["Domingo","Lunes","Martes","Miércoles","Jueves","Viernes","Sábado"][d.getDay()];
  return `${wk}, ${d.getDate()} de ${monthsEs[d.getMonth()]} de ${d.getFullYear()}`;
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return ""; }
}

// -------- section renderers --------
function Row({ time, title, subtitle, right, testid }) {
  return (
    <li className="flex items-start justify-between gap-4 border-b border-slate-100 dark:border-slate-800 py-3 last:border-b-0" data-testid={testid}>
      <div className="flex items-start gap-3 min-w-0">
        {time && (
          <span className="text-[10px] font-mono text-slate-400 dark:text-slate-500 pt-0.5 shrink-0 w-10">{time}</span>
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{title}</div>
          {subtitle && <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">{subtitle}</div>}
        </div>
      </div>
      {right && <div className="shrink-0 text-right">{right}</div>}
    </li>
  );
}

function EmptySection({ label }) {
  return (
    <div className="py-10">
      <EmptyState icon={CalendarBlank} title="Sin actividad" message={`No hay ${label} para este día.`} compact />
    </div>
  );
}

function NewClientsList({ items }) {
  if (!items?.length) return <EmptySection label="altas de clientes" />;
  return (
    <ul>
      {items.map((c) => (
        <Row
          key={c.id}
          testid={`day-new-client-${c.id}`}
          time={fmtTime(c.created_at)}
          title={c.name || "—"}
          subtitle={[c.service_address || c.address, c.plan_name, c.rep_name].filter(Boolean).join(" · ")}
          right={<span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{c.status || "activo"}</span>}
        />
      ))}
    </ul>
  );
}

function InvoiceList({ items, showAmount = true, timeField = "created_at", label }) {
  if (!items?.length) return <EmptySection label={label} />;
  return (
    <ul>
      {items.map((inv) => (
        <Row
          key={inv.id}
          testid={`day-inv-${inv.id}`}
          time={fmtTime(inv[timeField])}
          title={inv.client_name || "—"}
          subtitle={inv.description || `Vence ${inv.due_date || ""}`}
          right={
            <div className="flex items-center gap-3">
              {showAmount && <span className="font-mono text-sm font-semibold text-slate-900 dark:text-white">{fmtMoney(inv.amount || 0)}</span>}
              <StatusBadge status={inv.status} />
            </div>
          }
        />
      ))}
    </ul>
  );
}

function CommsList({ items }) {
  if (!items?.length) return <EmptySection label="comunicaciones" />;
  return (
    <ul>
      {items.map((e) => (
        <Row
          key={e.id}
          testid={`day-email-${e.id}`}
          time={fmtTime(e.created_at)}
          title={e.subject || "(sin asunto)"}
          subtitle={`${e.to || "—"}${e.client_name ? " · " + e.client_name : ""}${e.type ? " · " + e.type : ""}`}
          right={
            e.mocked ? <span className="text-[10px] uppercase text-slate-400">simulado</span>
              : e.sent ? <span className="text-[10px] uppercase text-green-600 font-semibold">enviado</span>
              : <span className="text-[10px] uppercase text-red-600 font-semibold">falló</span>
          }
        />
      ))}
    </ul>
  );
}

function ActivityList({ items, label, testPrefix }) {
  if (!items?.length) return <EmptySection label={label} />;
  return (
    <ul>
      {items.map((a, idx) => (
        <Row
          key={`${testPrefix}-${idx}`}
          testid={`day-${testPrefix}-${idx}`}
          time={fmtTime(a.created_at)}
          title={a.client_name || a.message || "—"}
          subtitle={a.message && a.client_name ? a.message : a.disconnection_reason_label || a.new_status || ""}
        />
      ))}
    </ul>
  );
}

// -------- main component --------
export default function CalendarDayModal({ open, onOpenChange, date, userRole }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("clients");

  useEffect(() => {
    if (!open || !date) return;
    setLoading(true);
    setData(null);
    api.get(`/calendar/day-detail?date=${date}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ sections: {}, counts: {} }))
      .finally(() => setLoading(false));
  }, [open, date]);

  const sections = data?.sections || {};
  const counts = data?.counts || {};

  const seesFinance = userRole !== "customer_service";
  const seesOps = userRole !== "admin";

  // Build tab list dynamically based on role
  const tabs = [
    { key: "clients", label: "Clientes nuevos", count: counts.new_clients, show: true },
    { key: "invoices", label: "Facturas emitidas", count: counts.invoices_issued, show: seesFinance },
    { key: "payments", label: "Pagos recibidos", count: counts.payments_received, show: seesFinance },
    { key: "due", label: "Vencen hoy", count: counts.invoices_due, show: seesFinance },
    { key: "comms", label: "Comunicaciones", count: counts.communications, show: true },
    { key: "ops", label: "Operaciones", count: (counts.disconnections || 0) + (counts.reconnections || 0) + (counts.outages || 0) + (counts.visits || 0), show: seesOps },
  ].filter((t) => t.show);

  // Ensure current tab is always valid
  useEffect(() => {
    if (!tabs.find((t) => t.key === tab)) setTab(tabs[0]?.key || "clients");
  }, [tabs, tab]);

  const total = tabs.reduce((s, t) => s + (t.count || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
        data-testid="day-detail-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-800">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 font-semibold mb-1">Detalle del día</div>
          <DialogTitle className="text-lg font-semibold text-slate-900 dark:text-white">
            {formatSpanishDate(date)}
          </DialogTitle>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1" data-testid="day-total-events">
            {loading ? "Cargando…" : `${total} evento${total === 1 ? "" : "s"} registrado${total === 1 ? "" : "s"}`}
          </div>
        </DialogHeader>

        <div className="px-6 pb-6 pt-4">
          {loading ? (
            <div className="py-16 text-center text-sm text-slate-500 dark:text-slate-400">Cargando actividad…</div>
          ) : (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="flex flex-wrap gap-1 h-auto bg-slate-100 dark:bg-slate-800 p-1">
                {tabs.map((t) => (
                  <TabsTrigger
                    key={t.key}
                    value={t.key}
                    className="text-xs data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900"
                    data-testid={`day-tab-${t.key}`}
                  >
                    {t.label}
                    <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-slate-900/10 dark:bg-white/10 text-[10px] font-mono font-semibold">
                      {t.count || 0}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="max-h-[55vh] overflow-y-auto mt-4">
                <TabsContent value="clients"><NewClientsList items={sections.new_clients} /></TabsContent>
                {seesFinance && <TabsContent value="invoices"><InvoiceList items={sections.invoices_issued} label="facturas emitidas" /></TabsContent>}
                {seesFinance && <TabsContent value="payments"><InvoiceList items={sections.payments_received} timeField="paid_at" label="pagos recibidos" /></TabsContent>}
                {seesFinance && <TabsContent value="due"><InvoiceList items={sections.invoices_due} timeField="due_date" label="facturas que vencen hoy" /></TabsContent>}
                <TabsContent value="comms"><CommsList items={sections.communications} /></TabsContent>
                {seesOps && (
                  <TabsContent value="ops">
                    <OpsSection
                      disconnections={sections.disconnections}
                      reconnections={sections.reconnections}
                      outages={sections.outages}
                      visits={sections.visits}
                    />
                  </TabsContent>
                )}
              </div>
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OpsSection({ disconnections, reconnections, outages, visits }) {
  const total = (disconnections?.length || 0) + (reconnections?.length || 0) + (outages?.length || 0) + (visits?.length || 0);
  if (!total) return <EmptySection label="operaciones" />;
  return (
    <div className="space-y-6">
      {disconnections?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-red-600 dark:text-red-400 font-semibold mb-2">Desconexiones · {disconnections.length}</div>
          <ActivityList items={disconnections} label="desconexiones" testPrefix="disc" />
        </div>
      )}
      {reconnections?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-green-700 dark:text-green-400 font-semibold mb-2">Reconexiones · {reconnections.length}</div>
          <ActivityList items={reconnections} label="reconexiones" testPrefix="recon" />
        </div>
      )}
      {outages?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 dark:text-amber-400 font-semibold mb-2">Cortes / Outages · {outages.length}</div>
          <ActivityList items={outages} label="cortes" testPrefix="outage" />
        </div>
      )}
      {visits?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-blue-700 dark:text-blue-400 font-semibold mb-2">Visitas / Conexiones agendadas · {visits.length}</div>
          <ActivityList items={visits} label="visitas" testPrefix="visit" />
        </div>
      )}
    </div>
  );
}
